import http from './http';
import { cleanParams } from './utils';

const ENDPOINTS = {
  APPOINTMENTS: '/api/v1/appointments',
  APPOINTMENT_BY_ID: (id) => `/api/v1/appointments/${id}`,
  APPOINTMENT_STATS: '/api/v1/appointments/stats',
  APPOINTMENT_COMPLETE: (id) => `/api/v1/appointments/${id}/complete`,
  BOOK_APPOINTMENT_WITH_SLOT: '/api/v1/booking/book-appointment',
};

/**
 * Appointment API Service
 * Note: Queue-related methods have been moved to queue.js
 */
export const appointmentAPI = {
  // Book appointment using a slot (new booking flow)
  bookWithSlot: async ({ patientId, doctorId, slotId, purpose, notes, complaint }) => {
    const payload = { patientId, doctorId, slotId };
    if (purpose) payload.purpose = purpose;
    if (notes) payload.notes = notes;
    if (complaint) payload.complaint = complaint;
    const response = await http.post(ENDPOINTS.BOOK_APPOINTMENT_WITH_SLOT, payload);
    return response.data; // { success, message, data }
  },

  // Create a new appointment
  create: async (appointmentData) => {
    const response = await http.post(ENDPOINTS.APPOINTMENTS, appointmentData);
    return response.data;
  },

  // Schedule appointment with validation
  schedule: async (appointmentDataToSchedule) => {
    // Validate required fields
    if (!appointmentDataToSchedule.patient || !appointmentDataToSchedule.doctor || 
        !appointmentDataToSchedule.appointmentDate || !appointmentDataToSchedule.appointmentTime || 
        !appointmentDataToSchedule.purpose) {
      throw new Error('Please fill in all required appointment fields');
    }

    // Validate appointment date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(appointmentDataToSchedule.appointmentDate);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      throw new Error('Appointment date cannot be in the past');
    }

    // Validate purpose field length
    if (!appointmentDataToSchedule.purpose || appointmentDataToSchedule.purpose.trim().length < 3) {
      throw new Error('Purpose must be at least 3 characters');
    }

    // Prepare appointment payload for backend
    const appointmentPayload = {
      patientId: appointmentDataToSchedule.patient,
      doctorId: appointmentDataToSchedule.doctor,
      appointmentDate: appointmentDataToSchedule.appointmentDate, // YYYY-MM-DD
      appointmentTime: appointmentDataToSchedule.appointmentTime, // HH:mm
      durationMin: parseInt(appointmentDataToSchedule.duration || 30, 10),
      purpose: appointmentDataToSchedule.purpose,
      notes: appointmentDataToSchedule.note || undefined,
      withArm: false
    };

    const response = await http.post(ENDPOINTS.APPOINTMENTS, appointmentPayload);
    return response.data;
  },

  // Get appointment by ID
  getById: async (id) => {
    const response = await http.get(ENDPOINTS.APPOINTMENT_BY_ID(id));
    return response.data;
  },

  // Update appointment
  update: async (id, appointmentData) => {
    const response = await http.patch(ENDPOINTS.APPOINTMENT_BY_ID(id), appointmentData);
    return response.data;
  },

  // List appointments with filtering
  list: async (params = {}) => {
    // Clean parameters to remove empty values
    const cleanedParams = cleanParams(params);
    const response = await http.get(ENDPOINTS.APPOINTMENTS, { params: cleanedParams });
    return response.data;
  },

  // Get appointment statistics
  getStats: async () => {
    const response = await http.get(ENDPOINTS.APPOINTMENT_STATS);
    return response.data;
  },

  // Cancel appointment
  cancel: async (id) => {
    const response = await http.delete(ENDPOINTS.APPOINTMENT_BY_ID(id));
    return response.data;
  },

  // Complete appointment (mark as completed)
  complete: async (id) => {
    const response = await http.post(ENDPOINTS.APPOINTMENT_COMPLETE(id));
    return response.data;
  },

  // Delete appointment
  deleteAppointment: async (id) => {
    const response = await http.delete(ENDPOINTS.APPOINTMENT_BY_ID(id));
    return response.data;
  },

  // Get today's appointments
  getTodaysAppointments: async () => {
    const today = new Date().toISOString().split('T')[0];
    const response = await http.get(ENDPOINTS.APPOINTMENTS, { 
      params: { 
        date: today,
        includePatient: true,
        includeDoctor: true 
      } 
    });
    return response.data;
  },

  // Get appointments by date range
  getByDateRange: async (startDate, endDate) => {
    const response = await http.get(ENDPOINTS.APPOINTMENTS, {
      params: {
        startDate,
        endDate,
        includePatient: true,
        includeDoctor: true
      }
    });
    return response.data;
  },

  // Get appointments for a specific doctor
  // Backend expects query key 'doctor' (not 'doctorId')
  getByDoctor: async (doctorId, params = {}) => {
    const response = await http.get(ENDPOINTS.APPOINTMENTS, {
      params: {
        doctor: doctorId,
        includePatient: true,
        ...params
      }
    });
    return response.data;
  },

  // Get appointments for a specific patient
  // Backend expects query key 'patient' (not 'patientId')
  getByPatient: async (patientId, params = {}) => {
    const response = await http.get(ENDPOINTS.APPOINTMENTS, {
      params: {
        patient: patientId,
        includeDoctor: true,
        ...params
      }
    });
    return response.data;
  },

  // === QUEUE-RELATED METHODS (for backward compatibility) ===
  // These methods are deprecated - use queueAPI instead
  
  // Join appointment queue
  joinQueue: async (id) => {
    console.warn('appointmentAPI.joinQueue is deprecated. Use queueAPI.joinQueue instead.');
    const response = await http.post(`${ENDPOINTS.APPOINTMENT_BY_ID(id)}/join-queue`);
    return response.data;
  },

  // Start appointment service
  startService: async (id) => {
    console.warn('appointmentAPI.startService is deprecated. Use queueAPI.startService instead.');
    const response = await http.post(`${ENDPOINTS.APPOINTMENT_BY_ID(id)}/start-service`);
    return response.data;
  },

  // Complete appointment service
  completeService: async (id) => {
    console.warn('appointmentAPI.completeService is deprecated. Use queueAPI.completeService instead.');
    const response = await http.post(`${ENDPOINTS.APPOINTMENT_BY_ID(id)}/complete-service`);
    return response.data;
  },

  // Get doctor's queue (legacy method)
  getDoctorQueue: async (doctorId) => {
    console.warn('appointmentAPI.getDoctorQueue is deprecated. Use queueAPI.getQueueSummary instead.');
    const response = await http.get(`/api/v1/queue/doctor/${doctorId}/summary`);
    // Transform the response to match old format for backward compatibility
    if (response.data.success) {
      return {
        success: true,
        data: {
          queue: response.data.data.queueEntries || []
        }
      };
    }
    return response.data;
  },

  // Get next appointment in queue (legacy method)
  getNextInQueue: async (doctorId) => {
    console.warn('appointmentAPI.getNextInQueue is deprecated. Use queueAPI.getQueueSummary instead.');
    const response = await http.get(`/api/v1/queue/doctor/${doctorId}/summary`);
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data.queueSummary?.currentPatient || null
      };
    }
    return response.data;
  },

  // Get queue summary (legacy method)
  getQueueSummary: async (doctorId) => {
    console.warn('appointmentAPI.getQueueSummary is deprecated. Use queueAPI.getQueueSummary instead.');
    const response = await http.get(`/api/v1/queue/doctor/${doctorId}/summary`);
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data.queueSummary || {}
      };
    }
    return response.data;
  }
};

export default appointmentAPI;
